import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Divider,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Link,
  Switch,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import API_BASE_URL from 'config/api';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import ServiceAgreement from "components/ServiceAgreement";
import SalesAgreement from "components/SalesAgreement";
import InformationProtection from "components/InformationProtection";

import { BlueButton, WhiteButton } from "styles";

// ---- Shared payload builders (save-card & checkout) ----
const splitName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/);
  const name = parts[0] || "";
  const surname = parts.slice(1).join(" ") || "";
  return { name, surname };
};

const buildBuyer = ({
  fullName,
  identityNumber,
  phoneNumber,
  billing,
  email,
  ip = "85.34.78.112",
}) => {
  const { name, surname } = splitName(fullName);
  return {
    externalId: identityNumber || "N/A",
    name,
    surname,
    gsmNumber: phoneNumber,
    email,
    identityNumber,
    registrationAddress: billing?.address || "",
    ip,
    city: billing?.city || "",
    country: billing?.country || "Türkiye",
    zipCode: billing?.postalCode || "",
  };
};

const buildSaveCardPayload = (common, card) => {
  return {
    email: common.email,
    card: {
      cardHolderName: card.cardHolderName,
      cardNumber: (card.cardNumber || "").replace(/\s/g, ""),
      expireMonth: card.expireMonth,
      expireYear:
        card.expireYear && card.expireYear.length === 2
          ? `20${card.expireYear}`
          : card.expireYear,
      cvc: card.cvc,
    },
    buyer: buildBuyer({
      fullName: common.fullName,
      identityNumber: common.identityNumber,
      phoneNumber: common.phoneNumber,
      billing: common.billing,
      email: common.email,
    }),
  };
};

function buildCheckoutPayload(userInfo, paymentInfo) {
  const {
    fullName,
    phoneNumber,
    identityNumber,
    clientName,
    domain,
    billing,
    email,
    userId,
  } = userInfo;
  const { cardToken, cardUserKey, subscriptionPlanId } = paymentInfo;

  return {
    userId,
    fullName,
    phoneNumber,
    identityNumber,
    clientName,
    domain,
    billingInfo: billing,
    email,
    cardToken,
    cardUserKey,
    subscriptionPlanId,
  };
}

const AccountPurchase = () => {
  const formRef = useRef(null);
  useEffect(() => {
    const meStr = sessionStorage.getItem("userMeData");
    const authToken = sessionStorage.getItem("authToken");

    if (!meStr && authToken) {
      fetch(`${API_BASE_URL}/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((meJson) => {
          if (meJson) {
            sessionStorage.setItem("userMeData", JSON.stringify(meJson));
            if (meJson?.user?.name) setFullName(meJson.user.name);
            if (meJson?.user?.phoneNumber) setPhoneNumber(meJson.user.phoneNumber);
            if (meJson?.user?.clients?.[0]?.name)
              setClientName(meJson.user.clients[0].name);
          }
        })
        .catch((e) => console.error("userMeData fetch failed", e));
    } else if (meStr) {
      try {
        const me = JSON.parse(meStr);
        if (me?.user?.name) setFullName(me.user.name);
        if (me?.user?.phoneNumber) setPhoneNumber(me.user.phoneNumber);
        if (me?.user?.clients?.[0]?.name)
          setClientName(me.user.clients[0].name);
      } catch (e) {
        console.error("Invalid userMeData", e);
      }
    }
  }, []);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ---- Purchase form states ----
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [identityNumber, setIdentityNumber] = useState("");
  const [clientName, setClientName] = useState("");
  const [domain, setDomain] = useState("");

  // Billing info
  const [billingForm, setBillingForm] = useState({
    name: "",
    address: "",
    city: "",
    district: "",
    postalCode: "",
    country: "Türkiye",
    taxOffice: "",
    taxNumber: "",
    state: "",
    eInvoiceEnabled: false,
  });

  // Card info (used for save-card)
  const [card, setCard] = useState({
    cardHolderName: "",
    cardNumber: "",
    expireMonth: "",
    expireYear: "",
    cvc: "",
  });

  // Plan selection
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  // Agreements
  const [kvkkOpen, setKvkkOpen] = useState(false);
  const [tosOpen, setTosOpen] = useState(false);
  const [salesAgreementOpen, setSalesAgreementOpen] = useState(false);
  const [InformationProtectionOpen, setInformationProtectionOpen] = useState(false);
  const [agreeTos, setAgreeTos] = useState(false);
  const [agreeKvkk, setAgreeKvkk] = useState(false);
  const [isYearly, setIsYearly] = useState(false);

  // UI feedback
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Checkout final state
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);

  // Plan bilgileri
  const monthlyPlans = [
    {
      id: "2",
      title: "Basic",
      priceText: "₺3.000/ay",
      features: ["Tek domain", "Temel raporlama", "E‑posta desteği"],
    },
    {
      id: "3",
      title: "Advanced",
      priceText: "₺3.500/ay",
      features: ["Çoklu dil", "Gelişmiş raporlama", "Öncelikli destek"],
    },
    {
      id: "4",
      title: "Lansman Özel",
      priceText: "₺3.000/ay",
      features: ["Çoklu dil", "Aylık Ödeme", "1 Yıl Fiyat Koruma"],
    }
  ];
  const yearlyPlans = [
    {
      id: "5",
      title: "Basic",
      priceText: "₺30.000/yıl",
      features: ["Tek domain", "Temel raporlama", "E‑posta desteği"],
    },
    {
      id: "6",
      title: "Advanced",
      priceText: "₺35.000/yıl",
      features: ["Çoklu dil", "Gelişmiş raporlama", "Öncelikli destek"],
    },
  ];
  const plans = isYearly ? yearlyPlans : monthlyPlans;

  // Helper: required fields
  const requiredFields = [
    ["fullName", fullName],
    ["phoneNumber", phoneNumber],
    ["identityNumber", identityNumber],
    ["clientName", clientName],
    ["domain", domain],
    ["billing.name", billingForm.name],
    ["billing.address", billingForm.address],
    ["billing.city", billingForm.city],
    ["billing.district", billingForm.district],
    ["billing.postalCode", billingForm.postalCode],
    ["card.cardHolderName", card.cardHolderName],
    ["card.cardNumber", card.cardNumber],
    ["card.expireMonth", card.expireMonth],
    ["card.expireYear", card.expireYear],
    ["card.cvc", card.cvc],
  ];

  const validateRequired = () => {
    const missing = requiredFields.filter(
      ([, v]) => String(v ?? "").trim() === ""
    );
    if (missing.length) {
      setShowErrors(true);
      setErrorMsg("Lütfen tüm zorunlu alanları doldurun.");
      return false;
    }
    if (!selectedPlanId) {
      setShowErrors(true);
      setErrorMsg("Lütfen bir plan seçin.");
      return false;
    }
    if (!agreeTos || !agreeKvkk) {
      setShowErrors(true);
      setErrorMsg("Lütfen sözleşmeleri onaylayın.");
      return false;
    }
    return true;
  };

  // Refresh cached session keys after successful purchase
  const refreshUserCaches = async () => {
    try {
      const authToken = sessionStorage.getItem("authToken");
      // 1) /me -> userMeData
      const meRes = await fetch(`${API_BASE_URL}/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (meRes.ok) {
        const meJson = await meRes.json();
        sessionStorage.setItem("userMeData", JSON.stringify(meJson));
        // Try to refresh userCookieSummary by tenant (best‑effort)
        try {
          // Prefer tenantId from user clients; fallback to session token cache if you keep it elsewhere
          const tenantId =
            meJson?.user?.clients?.[0]?.clientId ||
            sessionStorage.getItem("tenantId");

          if (tenantId) {
            const sumRes = await fetch(
              `${API_BASE_URL}/cookie-definition/summary?tenantId=${tenantId}`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (sumRes.ok) {
              const sumJson = await sumRes.json();
              const summaries = { [tenantId]: sumJson?.data || sumJson };
              sessionStorage.setItem(
                "userCookieSummary",
                JSON.stringify(summaries)
              );
            }
          }
        } catch (_) {}
      }
    } catch (_) {
      // best-effort refresh; ignore
    }
  };

  
  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateRequired()) return;
    setShowErrors(false);
    setSubmitting(true);
    setErrorMsg("");
    try {
      const meStrForEmail = sessionStorage.getItem("userMeData");
      const meObj = meStrForEmail ? JSON.parse(meStrForEmail) : null;
      const emailForCard =
        sessionStorage.getItem("userEmail") ||
        meObj?.user?.email ||
        "checkouttest@example.com";

  const paymentData = {
    fullName,
    phoneNumber,
    identityNumber,
    clientName,
    domain,
    billingInfo: billingForm,
    email: emailForCard,
    card: {
      cardHolderName: card.cardHolderName,
      cardNumber: (card.cardNumber || "").replace(/\s/g, ""),
      expireMonth: card.expireMonth,
      expireYear:
        card.expireYear && card.expireYear.length === 2
          ? `20${card.expireYear}`
          : card.expireYear,
      cvc: card.cvc,
    },
    subscriptionPlanId: selectedPlanId,
  };
      await initiateNewSubscription(paymentData);
      // Success/failure handled inside initiateNewSubscription
    } catch (err) {
      setErrorMsg(err.message || "Bir hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  // UI
  const FormCard = (
    <Card sx={{ boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Yeni Site Satın Al
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box
          component="form"
          id="purchase-form"
          ref={formRef}
          onSubmit={handleSubmit}
          autoComplete="on"
        >
          <Grid container spacing={2}>
            {/* Account / owner */}
            {/* Client / domain */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Domain (örn. example.com)"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                error={showErrors && String(domain).trim() === ""}
              />
            </Grid>

            {/* Billing */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                Fatura Bilgileri
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ticari Ünvan / Ad Soyad"
                value={billingForm.name}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, name: e.target.value })
                }
                error={showErrors && String(billingForm.name).trim() === ""}
                autoComplete="name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Adres"
                value={billingForm.address}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, address: e.target.value })
                }
                error={showErrors && String(billingForm.address).trim() === ""}
                autoComplete="address-line1"
              />
            </Grid>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="İlçe"
                  value={billingForm.district}
                  onChange={(e) =>
                    setBillingForm({ ...billingForm, district: e.target.value })
                  }
                  error={
                    showErrors && String(billingForm.district).trim() === ""
                  }
                  autoComplete="address-level2"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="İl"
                  value={billingForm.city}
                  onChange={(e) =>
                    setBillingForm({ ...billingForm, city: e.target.value })
                  }
                  error={showErrors && String(billingForm.city).trim() === ""}
                  autoComplete="address-level1"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Posta Kodu"
                  value={billingForm.postalCode}
                  onChange={(e) =>
                    setBillingForm({
                      ...billingForm,
                      postalCode: e.target.value,
                    })
                  }
                  error={
                    showErrors && String(billingForm.postalCode).trim() === ""
                  }
                  autoComplete="postal-code"
                />
              </Grid>
            </Grid>
            {/* 3. satır: Vergi Dairesi + TCKN/Vergi No */}
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vergi No / TCKN"
                  value={identityNumber}
                  onChange={(e) => {
                    const only = (e.target.value || "")
                      .replace(/\D/g, "")
                      .slice(0, 11);
                    setIdentityNumber(only);
                    setBillingForm((prev) => ({ ...prev, taxNumber: only }));
                  }}
                  onPaste={(e) => {
                    const text =
                      (e.clipboardData || window.clipboardData).getData(
                        "text"
                      ) || "";
                    const only = text.replace(/\D/g, "").slice(0, 11);
                    e.preventDefault();
                    setIdentityNumber(only);
                    setBillingForm((prev) => ({ ...prev, taxNumber: only }));
                  }}
                  inputProps={{ maxLength: 11, inputMode: "numeric" }}
                  error={showErrors && String(identityNumber).trim() === ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vergi Dairesi"
                  value={billingForm.taxOffice}
                  onChange={(e) =>
                    setBillingForm({
                      ...billingForm,
                      taxOffice: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
            {/* 4. satır: İlçe + İl + Posta Kodu */}

            {/* Card */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                Kart Bilgileri
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kart Sahibi"
                value={card.cardHolderName}
                onChange={(e) =>
                  setCard({ ...card, cardHolderName: e.target.value })
                }
                error={showErrors && String(card.cardHolderName).trim() === ""}
                name="cc-name"
                autoComplete="cc-name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Kart numarası yardımcı fonksiyonları */}
              {/*
                Kart numarası için marka tespiti ve uygun formatlama.
              */}
              {(() => {
                // keep helpers in block scope for clarity in JSX
                // (but we'll hoist them out for better performance below)
                return null;
              })()}
              {/* --- Card number helpers (inserted above TextField) --- */}
              {/*
                detectCardBrand & formatCardNumber helpers are declared above.
              */}
              {/* --- */}
              {/* Helper functions for card number formatting */}
              {/*
                The following helpers are inserted here as per instruction.
              */}
              {/*
              const detectCardBrand = (digits) => {
                if (/^3[47]\d{0,13}$/.test(digits)) return "amex"; // 34 or 37
                if (/^4\d{0,18}$/.test(digits)) return "visa";
                if (/^5[1-5]\d{0,14}$/.test(digits) || /^2(2[2-9]|[3-6]\d|7[01]|720)\d{0,12}$/.test(digits)) return "mastercard"; // incl. 2221–2720
                if (/^(5018|5020|5038|5612|5893|6304|6759|6761|6763)\d{0,15}$/.test(digits)) return "maestro";
                return "generic";
              };

              const formatCardNumber = (value) => {
                const digits = (value || "").replace(/\D/g, "");
                const brand = detectCardBrand(digits);
                let parts = [];
                if (brand === "amex") {
                  // 4-6-5 grouping for up to 15 digits
                  parts = [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)];
                } else {
                  // default 4-4-4-4-3 (up to 19 digits total)
                  parts = [
                    digits.slice(0, 4),
                    digits.slice(4, 8),
                    digits.slice(8, 12),
                    digits.slice(12, 16),
                    digits.slice(16, 19),
                  ];
                }
                return parts.filter(Boolean).join(" ").trim();
              };
              */}
              {/* --- */}
              <TextField
                fullWidth
                label="Kart Numarası"
                value={card.cardNumber}
                onChange={(e) => {
                  const grouped = formatCardNumber(e.target.value);
                  setCard({ ...card, cardNumber: grouped });
                }}
                onPaste={(e) => {
                  const text = (
                    e.clipboardData || window.clipboardData
                  ).getData("text");
                  const grouped = formatCardNumber(text);
                  e.preventDefault();
                  setCard({ ...card, cardNumber: grouped });
                }}
                inputProps={{ inputMode: "numeric", maxLength: 23 }}
                error={showErrors && String(card.cardNumber).trim() === ""}
                name="cc-number"
                autoComplete="cc-number"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Ay (MM)"
                value={card.expireMonth}
                onChange={(e) => {
                  const raw = (e.target.value || "")
                    .replace(/\D/g, "")
                    .slice(0, 2);
                  let mm = raw;
                  if (raw.length === 1) {
                    if (raw === "0") {
                      mm = "";
                    } else if (parseInt(raw, 10) > 1) {
                      mm = "0" + raw; // 2-9 => 02-09
                    }
                  } else if (raw.length === 2) {
                    const n = parseInt(raw, 10);
                    if (n === 0) {
                      mm = "";
                    } else if (n > 12) {
                      mm = "12";
                    } else {
                      mm = raw;
                    }
                  }
                  setCard({ ...card, expireMonth: mm });
                }}
                onPaste={(e) => {
                  const text =
                    (e.clipboardData || window.clipboardData).getData("text") ||
                    "";
                  const raw = text.replace(/\D/g, "").slice(0, 2);
                  let mm = raw;
                  if (raw.length === 1) {
                    if (raw === "0") {
                      mm = "";
                    } else if (parseInt(raw, 10) > 1) {
                      mm = "0" + raw;
                    }
                  } else if (raw.length === 2) {
                    const n = parseInt(raw, 10);
                    if (n === 0) {
                      mm = "";
                    } else if (n > 12) {
                      mm = "12";
                    } else {
                      mm = raw;
                    }
                  }
                  e.preventDefault();
                  setCard({ ...card, expireMonth: mm });
                }}
                inputProps={{ maxLength: 2, inputMode: "numeric" }}
                error={showErrors && String(card.expireMonth).trim() === ""}
                name="cc-exp-month"
                autoComplete="cc-exp-month"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Yıl (20YY)"
                placeholder="YY"
                value={card.expireYear}
                onChange={(e) => {
                  const only = (e.target.value || "")
                    .replace(/\D/g, "")
                    .slice(0, 2);
                  setCard({ ...card, expireYear: only });
                }}
                onPaste={(e) => {
                  const text =
                    (e.clipboardData || window.clipboardData).getData("text") ||
                    "";
                  const only = text.replace(/\D/g, "").slice(0, 2);
                  e.preventDefault();
                  setCard({ ...card, expireYear: only });
                }}
                inputProps={{ maxLength: 2, inputMode: "numeric" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <Box
                        sx={{
                          fontFamily: "inherit",
                          fontSize: "0.9rem",
                          color: "text.primary",
                        }}
                      >
                        20
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputAdornment-root": { mr: 0 },
                  "& .MuiOutlinedInput-input": { paddingLeft: 0 },
                }}
                error={showErrors && String(card.expireYear).trim() === ""}
                name="cc-exp-year"
                autoComplete="cc-exp-year"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="CVC"
                value={card.cvc}
                onChange={(e) => {
                  const only = (e.target.value || "")
                    .replace(/\D/g, "")
                    .slice(0, 3);
                  setCard({ ...card, cvc: only });
                }}
                onPaste={(e) => {
                  const text =
                    (e.clipboardData || window.clipboardData).getData("text") ||
                    "";
                  const only = text.replace(/\D/g, "").slice(0, 3);
                  e.preventDefault();
                  setCard({ ...card, cvc: only });
                }}
                inputProps={{ maxLength: 3, inputMode: "numeric" }}
                error={showErrors && String(card.cvc).trim() === ""}
                name="cc-csc"
                autoComplete="cc-csc"
              />
            </Grid>

            {/* Agreements and purchase button moved to PlansCard */}
          </Grid>
          {/* 3DSecure iframe container */}
          <div id="3ds-container" style={{ marginTop: "20px" }}></div>
          {/* (Legacy/other 3DS) */}
          <div id="threeDSIframeContainer" style={{ marginTop: "20px" }}></div>
        </Box>
      </CardContent>
    </Card>
  );

  const SuccessCard = (
    <Card sx={{ boxShadow: 3, textAlign: "center" }}>
      <CardContent>
        <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Ödeme Başarılı
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Ödemeniz başarıyla alındı ve site hesabınıza eklendi.
        </Typography>
        <Box display="flex" justifyContent="center">
          <BlueButton onClick={() => navigate("/domains")}>Tamam</BlueButton>
        </Box>
      </CardContent>
    </Card>
  );

  const PlansCard = (
    <Card sx={{ boxShadow: 3, position: "sticky", top: theme.spacing(2) }}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          {/* Left: Section title */}
          <Typography variant="h6" fontWeight={700}>
            Planını Seç
          </Typography>

          {/* Right: Cycle switch */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" sx={{ lineHeight: 1 }}>
              {isYearly ? "Yıllık" : "Aylık"}
            </Typography>
            <Switch
              size="small"
              checked={isYearly}
              onChange={(e) => {
                setIsYearly(e.target.checked);
                setExpanded(null);
                setSelectedPlanId(null);
              }}
              inputProps={{ "aria-label": "Faturalama dönemi" }}
            />
          </Box>
        </Box>
        {plans.map((p) => (
          <Accordion
            key={p.id}
            expanded={expanded === p.id}
            onChange={(_, isExp) => setExpanded(isExp ? p.id : null)}
            sx={{
              mb: 1,
              borderRadius: 2,
              border:
                selectedPlanId === p.id
                  ? `2px solid #1976d2`
                  : "1px solid transparent",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => ({
                borderRadius: 1,
              })}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Box>
                  <Typography fontWeight={700}>{p.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(() => {
                      const m = (p.priceText || "").match(
                        /₺\s*([0-9.,]+)\/(ay|yıl)/i
                      );
                      if (!m) return p.priceText;
                      const total =
                        Number(m[1].replace(/\./g, "").replace(/,/g, ".")) || 0; // p.priceText KDV DAHİL toplam
                      const formatted = total.toLocaleString("tr-TR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                      const cycle = m[2].toLowerCase() === "yıl" ? "yıl" : "ay";
                      return `₺${formatted}/${cycle}`;
                    })()}
                  </Typography>
                </Box>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlanId(p.id);
                    setExpanded(p.id);
                  }}
                  color="inherit"
                  sx={(theme) => ({
                    minWidth: 112,
                    color: theme.palette.common.white,
                    bgcolor: selectedPlanId === p.id ? "#1976d2" : "#262628",
                    "&:hover": {
                      bgcolor: selectedPlanId === p.id ? "#1976d2" : "#262628",
                    },
                    boxShadow: "none",
                  })}
                  variant="contained"
                >
                  {selectedPlanId === p.id ? "Seçildi" : "Seçin"}
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{ bgcolor: "grey.200", borderRadius: 2, p: 1.5, m: 2 }}
            >
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {p.features.map((f, i) => (
                  <li key={i}>
                    <Typography variant="body2">{f}</Typography>
                  </li>
                ))}
              </ul>
            </AccordionDetails>
          </Accordion>
        ))}
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ mt: 1 }}>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            alignItems="flex-start"
            lineHeight={0.5}
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              sx={{ m: 0, alignItems: "center" }}
              control={
                <Checkbox
                  size="small"
                  checked={agreeTos}
                  onChange={(e) => setAgreeTos(e.target.checked)}
                  color={showErrors && !agreeTos ? "error" : "primary"}
                />
              }
              label={
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    display: "inline",
                    lineHeight: 1.5,
                    color:
                      showErrors && !agreeTos ? "error.main" : "text.primary",
                  }}
                >
                  <Link
                    component="body2"
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTosOpen(true);
                    }}
                    sx={{ fontSize: "0.875rem", fontWeight: 700 }}
                  >
                    Hizmet Sözleşmesi
                  </Link>
                  <span> ve </span>
                  <Link
                    component="body2"
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSalesAgreementOpen(true);
                    }}
                    sx={{ fontSize: "0.875rem", fontWeight: 700 }}
                  >
                    Mesafeli Satış Sözleşmesi
                  </Link>{" "}
                  ’ni okudum ve kabul ediyorum.
                </Typography>
              }
              labelPlacement="end"
            />
            <FormControlLabel
              sx={{ m: 0, alignItems: "center" }}
              control={
                <Checkbox
                  size="small"
                  checked={agreeKvkk}
                  onChange={(e) => setAgreeKvkk(e.target.checked)}
                  color={showErrors && !agreeKvkk ? "error" : "primary"}
                />
              }
              label={
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    display: "inline",
                    lineHeight: 1.5,
                    color:
                      showErrors && !agreeKvkk ? "error.main" : "text.primary",
                  }}
                >
                  <Link
                    component="body2"
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setKvkkOpen(true);
                    }}
                    sx={{ fontSize: "0.875rem", fontWeight: 700 }}
                  >
                    Kişisel Verilerin Korunması Kanunu Aydınlatma Metni
                  </Link>{" "}
                  ’ni okudum, anladım ve kabul ediyorum.
                </Typography>
              }
              labelPlacement="end"
            />
          </Box>

          <BlueButton
            type="button"
            startIcon={<CheckCircleIcon />}
            disabled={submitting}
            fullWidth={isMobile}
            onClick={() => {
              if (formRef.current) {
                // Trigger the existing form submit handler
                formRef.current.requestSubmit();
              }
            }}
          >
            {submitting ? "İşleniyor..." : "Satın Al"}
          </BlueButton>
        </Box>
      </CardContent>
    </Card>
  );

  // --- Helper functions for card number formatting ---
  const detectCardBrand = (digits) => {
    if (/^3[47]\d{0,13}$/.test(digits)) return "amex"; // 34 or 37
    if (/^4\d{0,18}$/.test(digits)) return "visa";
    if (
      /^5[1-5]\d{0,14}$/.test(digits) ||
      /^2(2[2-9]|[3-6]\d|7[01]|720)\d{0,12}$/.test(digits)
    )
      return "mastercard"; // incl. 2221–2720
    if (/^(5018|5020|5038|5612|5893|6304|6759|6761|6763)\d{0,15}$/.test(digits))
      return "maestro";
    return "generic";
  };

  const formatCardNumber = (value) => {
    const digits = (value || "").replace(/\D/g, "");
    const brand = detectCardBrand(digits);
    let parts = [];
    if (brand === "amex") {
      // 4-6-5 grouping for up to 15 digits
      parts = [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)];
    } else {
      // default 4-4-4-4-3 (up to 19 digits total)
      parts = [
        digits.slice(0, 4),
        digits.slice(4, 8),
        digits.slice(8, 12),
        digits.slice(12, 16),
        digits.slice(16, 19),
      ];
    }
    return parts.filter(Boolean).join(" ").trim();
  };
  // ---------------------------------------------------
  // --- Logicookie: 3DS sonrası abonelik tamamla helper (component scope) ---
  const completeSubscriptionAfter3DS = async ({ paymentId, cardToken, cardUserKey }) => {
    console.log('[SUB:create] enter with:', { paymentId, cardToken, cardUserKey });
    const sessionId = sessionStorage.getItem('payment_session_id');
    console.log('[SUB:create] sessionId:', sessionId);
    if (!sessionId) {
      setErrorMsg('3DS oturumu bulunamadı. Lütfen tekrar deneyin.');
      return;
    }
    try {
      console.log('[SUB:create] POST /payment/complete-subscription body:', { paymentId, sessionId, cardToken, cardUserKey });
      const res = await fetch(`${API_BASE_URL}/payment/complete-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ paymentId, sessionId, cardToken, cardUserKey })
      });
      const json = await res.json();
      console.log('[SUB:create] response:', { status: res.status, ok: res.ok, json });
      if (!res.ok) throw new Error(json?.error || 'Abonelik oluşturma başarısız.');

      // Temizlik ve UI güncelleme
      sessionStorage.removeItem('payment_session_id');
      setCheckoutSuccess(true);
      setSuccessMsg('Ödeme ve abonelik başarıyla tamamlandı.');
      try { await refreshUserCaches(); } catch (_) {}
    } catch (err) {
      console.error('complete-subscription hatası:', err);
      setErrorMsg(err.message || 'complete-subscription sırasında hata oluştu');
    }
  };

  // --- Logicookie 3DS/Subscription: initiateNewSubscription ---
  const initiateNewSubscription = async (paymentData) => {
    try {
      const {
        fullName,
        phoneNumber,
        identityNumber,
        clientName,
        domain,
        billingInfo,
        email,
        card,
        subscriptionPlanId,
      } = paymentData;
      const body = {
        fullName,
        phoneNumber,
        identityNumber,
        clientName,
        domain,
        billingInfo,
        email,
        cardHolderName: card.cardHolderName,
        cardNumber: (card.cardNumber || '').replace(/\s/g, ''),
        expireMonth: card.expireMonth,
        expireYear: card.expireYear,
        cvc: card.cvc,
        subscriptionPlanId,
        saveCard: true,
      };

      const response = await fetch(`${API_BASE_URL}/payment/checkout-auth-3ds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();
      console.log('[3DS:init] result from checkout-auth-3ds:', result);

      if (result.status === '3ds_required') {
        console.log('[3DS:init] 3ds_required. ids:', { sessionId: result.sessionId, conversationId: result.conversationId, paymentId: result.paymentId });
        const sid = result.sessionId || result.conversationId || result.paymentId;
        if (sid) sessionStorage.setItem('payment_session_id', sid);
        show3DSModal(result);
      } else if (result.error) {
        console.warn('[3DS:init] error:', result);
        setErrorMsg('Ödeme başarısız: ' + result.error);
      } else {
        setSuccessMsg('Ödeme başarılı!');
      }
    } catch (error) {
      console.error('Abonelik başlatma hatası:', error);
      setErrorMsg('Bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  // --- 3DS Modal & postMessage handler (component scope) ---
  const show3DSModal = (threeDSData) => {
    // Helper to show a short status message inside the 3DS modal before closing
    const show3DSStatus = (text) => {
      try {
        const body = document.querySelector('#threeds-modal .modal-body');
        if (!body) return;
        // remove previous notice if any
        const old = body.querySelector('.threeds-notice');
        if (old) old.remove();
        const notice = document.createElement('div');
        notice.className = 'threeds-notice';
        notice.style.position = 'absolute';
        notice.style.top = '0';
        notice.style.left = '0';
        notice.style.right = '0';
        notice.style.bottom = '0';
        notice.style.display = 'flex';
        notice.style.alignItems = 'center';
        notice.style.justifyContent = 'center';
        notice.style.background = 'rgba(255,255,255,0.95)';
        notice.style.textAlign = 'center';
        notice.style.padding = '24px';
        notice.style.fontSize = '16px';
        notice.style.lineHeight = '1.4';
        notice.innerHTML = `<div>
          <div style="font-weight:700;margin-bottom:8px;">${text}</div>
          <div style="opacity:.8;">Pencere birkaç saniye içinde kapanacaktır…</div>
        </div>`;
        body.appendChild(notice);
      } catch (_) {}
    };
    const on3DSMessage = async (event) => {
      try {
        console.log('[3DS:cb] postMessage received:', event?.data);
        const data = event?.data || {};
        if (data && data.type === 'iyzico-3ds-done') {
          console.log('[3DS:cb] iyzico-3ds-done payload:', data);
          console.log('[3DS:cb] calling completeSubscriptionAfter3DS with:', { paymentId: data.paymentId, cardToken: data.cardToken, cardUserKey: data.cardUserKey, sessionId: sessionStorage.getItem('payment_session_id') });
          const cleanup = () => {
            window.removeEventListener('message', on3DSMessage);
            const m = document.getElementById('threeds-modal');
            if (m) m.remove();
          };

          if (data.status === 'success') {
            // Show success notice before closing
            show3DSStatus('Ödemeniz alınmıştır. Siparişiniz oluşturuluyor…');
            try {
              await completeSubscriptionAfter3DS({
                paymentId: data.paymentId,
                cardToken: data.cardToken,
                cardUserKey: data.cardUserKey,
              });
            } catch (_) {}
            setTimeout(cleanup, 2000);
          } else {
            // Show error notice briefly as well
            show3DSStatus('3D Secure doğrulaması tamamlanamadı. Lütfen tekrar deneyin.');
            setErrorMsg(data.error || '3DS doğrulaması başarısız. Lütfen tekrar deneyin.');
            setTimeout(cleanup, 2000);
          }
        }
      } catch (e) {
        console.error('3DS message handling error:', e);
      }
    };

    try { window.removeEventListener('message', on3DSMessage); } catch (_) {}

    const modal = document.createElement('div');
    modal.id = 'threeds-modal';
    modal.innerHTML = `
      <div class="modal-overlay" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8); display: flex; justify-content: center; align-items: center; z-index: 9999;">
        <div class="modal-content" style="background: white; width: 90%; max-width: 500px; height: 600px; border-radius: 10px; position: relative; overflow: hidden;">
          <div class="modal-header" style="padding: 15px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; color: #333;">3D Secure Doğrulama</h3>
            <button class="close-btn" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #999;">&times;</button>
          </div>
          <div class="modal-body" style="height: calc(100% - 60px); position: relative;">
            <iframe id="threeds-iframe" style="width: 100%; height: 100%; border: none;"></iframe>
            <div class="loading" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center; color: #666;">
              <div class="spinner" style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 10px;"></div>
              <span>3D Secure yükleniyor...</span>
            </div>
          </div>
        </div>
      </div>`;

    if (!document.getElementById('threeds-spinner-css')) {
      const style = document.createElement('style');
      style.id = 'threeds-spinner-css';
      style.textContent = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }

    document.body.appendChild(modal);
    window.addEventListener('message', on3DSMessage);

    modal.querySelector('.close-btn').onclick = () => modal.remove();
    modal.querySelector('.modal-overlay').onclick = (e) => {
      if (e.target.classList.contains('modal-overlay')) modal.remove();
    };

    const iframe = modal.querySelector('#threeds-iframe');
    iframe.onload = () => { modal.querySelector('.loading').style.display = 'none'; };
    console.log('[3DS:ui] iframe onload attached');

    setTimeout(() => {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      const decodedHtml = atob(threeDSData.threeDSHtmlContent);
      iframeDoc.open();
      iframeDoc.write(decodedHtml);
      iframeDoc.close();
      console.log('[3DS:ui] 3DS HTML injected into iframe');
    }, 100);
  };
  // --- Pricing helpers for SalesAgreement ---
  const getSelectedPlan = () => {
    const list = isYearly ? yearlyPlans : monthlyPlans;
    return list.find((p) => p.id === selectedPlanId) || null;
  };

  const extractPrice = (priceText) => {
    const m = (priceText || "").match(/₺\s*([0-9.,]+)/);
    if (!m) return 0;
    const normalized = m[1].replace(/\./g, "").replace(/,/g, ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  const formatTL = (n) =>
    (n ?? 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const buildSalesAgreementProps = () => {
    const plan = getSelectedPlan();
    if (!plan) return {};
    // p.priceText içindeki sayı artık KDV DAHİL toplamdır
    const totalIncl = extractPrice(plan.priceText);
    const subtotal = +(totalIncl / 1.2).toFixed(2);
    const vat = +(totalIncl - subtotal).toFixed(2);

    return {
      planName: plan.title,
      durationLabel: isYearly ? "Yıllık" : "Aylık",
      subtotalText: formatTL(subtotal),
      vatText: formatTL(vat),
      totalText: formatTL(totalIncl),
    };
  };
  // ---------------------------------------------------
  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center">
          {checkoutSuccess ? (
            <Grid item xs={12} lg={7}>
              {SuccessCard}
            </Grid>
          ) : (
            <>
              <Grid item xs={12} lg={5} order={{ xs: 1, md: 1, lg: 2 }}>
                {PlansCard}
              </Grid>
              <Grid item xs={12} lg={7} order={{ xs: 2, md: 2, lg: 1 }}>
                {FormCard}
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>

      {/* TOS Modal */}
      <Dialog
        open={tosOpen}
        onClose={() => setTosOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent dividers>
          <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 1 }}>
            <ServiceAgreement />
          </Box>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setTosOpen(false)}>Kapat</WhiteButton>
        </DialogActions>
      </Dialog>

      {/* Sales Agreement Modal */}
      <Dialog
        open={salesAgreementOpen}
        onClose={() => setSalesAgreementOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent dividers>
          <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 1 }}>
            <SalesAgreement {...buildSalesAgreementProps()} />
          </Box>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setSalesAgreementOpen(false)}>
            Kapat
          </WhiteButton>
        </DialogActions>
      </Dialog>

      {/* KVKK Modal */}
      <Dialog
        open={kvkkOpen}
        onClose={() => setKvkkOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent dividers>
          <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 1 }}>
            <InformationProtection />
          </Box>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setKvkkOpen(false)}>Kapat</WhiteButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMsg("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMsg("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
};
export default AccountPurchase;